/**
 * Fades content blocks in as they scroll into view (see appear-effects.css).
 *
 * Only content that is below the fold when the page loads is hidden, so nothing above the fold is delayed and
 * nothing is hidden if this script does not run. Blocks that come into view together appear one after another.
 *
 * The script does nothing in the theme editor, with reduced motion, or without IntersectionObserver.
 */
const STAGGER_MS = 80;
const MAX_STAGGERED = 5;

/** Blocks in sections, product list headers, product cards and product list carousels (which are product cards on mobile) */
const TARGETS = [
  '.section-content-wrapper > *',
  '.section-resource-list__header',
  '.resource-list__item',
  '.resource-list__carousel',
  '.blog-post-item',
].join(',');

/** Things that should not fade: spacers, third party app blocks and other carousels */
const SKIP = [
  '.spacer-block',
  '.shopify-app-block',
  'slideshow-component:not(.resource-list__carousel)',
  'marquee-component',
  '[data-no-reveal]',
].join(',');

/** The sticky product page, and anything inside a carousel: cards slide in with the carousel and must not fade on their own */
const SKIP_WITHIN = '.product-information, slideshow-component, [data-no-reveal]';

const isDesignMode = Boolean(window.Shopify?.designMode);
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * @param {IntersectionObserverEntry[]} entries
 * @param {IntersectionObserver} observer
 */
function reveal(entries, observer) {
  const visible = entries.filter((entry) => entry.isIntersecting);

  // Top to bottom, so the stagger follows the reading order
  visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top || a.boundingClientRect.left - b.boundingClientRect.left);

  visible.forEach((entry, index) => {
    const element = /** @type {HTMLElement} */ (entry.target);

    element.style.setProperty('--reveal-delay', `${Math.min(index, MAX_STAGGERED) * STAGGER_MS}ms`);
    element.dataset.reveal = 'done';
    observer.unobserve(element);
  });
}

function init() {
  const observer = new IntersectionObserver(reveal, { rootMargin: '0px 0px -8% 0px' });

  document.querySelectorAll(`main .shopify-section :is(${TARGETS})`).forEach((node) => {
    const element = /** @type {HTMLElement} */ (node);

    if (element.matches(SKIP) || element.parentElement?.closest(SKIP_WITHIN)) return;

    // Already animating through a parent (matches come in document order): fading twice would stack the opacity
    if (element.parentElement?.closest('[data-reveal]')) return;

    // Already in view when the page loads, e.g. the first section: leave it alone
    if (element.getBoundingClientRect().top < window.innerHeight) return;

    element.dataset.reveal = 'pending';
    observer.observe(element);
  });
}

if (!isDesignMode && !prefersReducedMotion && 'IntersectionObserver' in window) {
  init();
}
